import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://gtbuyspreadsheet.run";
const logoUrl = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Dropshipping Sourcing System Explained — Spreadsheet Method",
  description: "A complete breakdown of how spreadsheet-based sourcing systems work for dropshipping businesses. Replace chaotic supplier management with a clean, repeatable framework.",
  datePublished: "2026-07-01",
  dateModified: "2026-07-01",
  author: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    logo: {
      "@type": "ImageObject",
      url: logoUrl,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${siteUrl}/dropshipping-sourcing-system`,
  },
};

export default function DropshippingSourcingSystem() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main>
        <section className="w-full pt-24 pb-12 md:pt-32 md:pb-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer flex items-center gap-1"><i className="ri-arrow-left-line"></i> Back to Home</Link>
              <span className="text-foreground-300">/</span>
              <span className="text-sm text-foreground-500">Guide</span>
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">Guide &middot; 6 min read</span>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">Dropshipping Sourcing System Explained — Spreadsheet Method</h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-2xl">A complete breakdown of how spreadsheet-based sourcing systems work for dropshipping businesses. Replace chaotic supplier management with a clean, repeatable framework that grows with your store.</p>
            <p className="text-xs text-foreground-400 mt-4">Published: July 2026</p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-[16/7] w-full rounded-2xl overflow-hidden bg-background-200">
              <img src="https://readdy.ai/api/search-image?query=Dropshipping%20ecommerce%20workspace%20with%20laptop%20displaying%20online%20store%20orders%20dashboard%20next%20to%20spreadsheet%20with%20supplier%20data%20shipping%20boxes%20and%20packaging%20materials%20on%20desk%20modern%20home%20office%20with%20brick%20wall%20warm%20industrial%20lighting%20productivity%20photography%20high%20detail&width=1600&height=700&seq=article-dropshipping-header&orientation=landscape"
                alt="Dropshipping Sourcing System" title="Dropshipping Sourcing System — Spreadsheet Method" className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </section>

        <article className="w-full px-6 md:px-10 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">The Dropshipping Sourcing Challenge</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">Dropshipping creates a unique sourcing problem: you manage products you never physically handle, working with suppliers you may never meet. Without a <strong>structured system for tracking supplier data</strong>, pricing discrepancies, stockouts, and quality issues pile up silently until they damage your store reputation.</p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">The spreadsheet method solves this by creating a single source of truth that connects every product, supplier, and order in one organized workspace.</p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">Method Comparison: Spreadsheet vs Manual</h2>
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tl-lg">Method</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tr-lg">Sourcing Efficiency</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Spreadsheet system (GTBuy method)", "High — all data centralized, auto-calculated"],
                    ["Manual email + memory tracking", "Low — scattered data, missed opportunities"],
                    ["Supplier platform dashboards only", "Medium — platform-limited, no cross-comparison"],
                    ["Hybrid: spreadsheet + platform APIs", "Very high — automated data import"],
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-background-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-foreground-700 font-medium border-b border-background-200">{row[0]}</td>
                      <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">Building Your Dropshipping Sourcing System</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">Start by mapping your current supplier list into a structured format. For each supplier, record not just prices but <strong>fulfillment speed, packaging quality notes, and communication responsiveness</strong>. These qualitative factors matter enormously in dropshipping, where the customer experience depends entirely on the supplier you chose.</p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">The key insight is that dropshipping success depends less on finding the cheapest supplier and more on finding the <strong>most reliable supplier at a fair price</strong>. A structured spreadsheet lets you balance both factors objectively instead of guessing.</p>

            <p className="text-sm text-foreground-500 italic mb-4">
              Further reading:{" "}
              <Link to="/why-use-spreadsheet-sourcing" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Why Use Spreadsheet Sourcing</Link>{" "}&middot;{" "}
              <Link to="/best-sourcing-tools" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Best Sourcing Tools</Link>{" "}&middot;{" "}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Homepage</Link>
            </p>
          </div>
        </article>

        <section className="relative w-full py-20 px-6 md:px-10 bg-foreground-950 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">Source Products for Your Dropshipping Store</h2>
            <p className="text-foreground-400 text-sm mb-6">KA Spreadsheet offers 14 product categories perfect for dropshipping. Start building your catalog today.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">GTBuy Sheet — Shop Now</a>
              <Link to="/" className="px-6 py-3 rounded-full bg-foreground-800 text-background-50 font-medium text-sm hover:bg-foreground-700 transition-colors cursor-pointer whitespace-nowrap">Back to Homepage</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}